import { jsPDF } from "jspdf"
import { encode } from "uqr"

interface ReportRow {
	label: string
	value: string
}

interface ReportSection {
	title: string
	rows: ReportRow[]
	highlight?: ReportRow
}

interface ReportMap {
	title: string
	image: string
}

export interface ParcelReportV2Content {
	parcelId: string
	title: string
	subtitle: string
	reportLabel: string
	planningTitle: string
	planningSource: string
	planningPoliciesLabel: string
	noPlanningPoliciesLabel: string
	planningRows: ReportRow[]
	constraintsLabel: string
	constraints: string[]
	noConstraintsLabel: string
	checkedLabel: string
	checkedConstraints: string[]
	metrics: ReportRow[]
	sections: ReportSection[]
	maps: ReportMap[]
	mapAttribution: string
	parcelUrl: string
	qrTitle: string
	qrDescription: string
	disclaimer: string
	footerBrand: string
	footerSource: string
}

const PAGE_WIDTH = 210
const MARGIN = 16
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2
const FOOTER_SPACE = 24
const GREEN = [13, 99, 73] as const
const BLACK = [35, 35, 35] as const
const GRAY = [105, 109, 112] as const
const LINE = [224, 225, 227] as const
const PALE = [248, 249, 249] as const

function lines(document: jsPDF, value: string, width: number): string[] {
	return document.splitTextToSize(value, width) as string[]
}

function header(document: jsPDF, content: ParcelReportV2Content, logoDataUrl: string): void {
	document.addImage(logoDataUrl, "PNG", MARGIN, 9, 38, 12.7)
	document.setFont("helvetica", "normal")
	document.setFontSize(9)
	document.setTextColor(...GRAY)
	document.text(`${content.reportLabel.toUpperCase()} · ${content.parcelId}`, PAGE_WIDTH - MARGIN, 17, {
		align: "right",
	})
	document.setDrawColor(...LINE)
	document.line(0, 26, PAGE_WIDTH, 26)
}

function heading(document: jsPDF, title: string, y: number, rightText?: string): number {
	document.setFont("helvetica", "bold")
	document.setFontSize(12)
	document.setTextColor(...GREEN)
	document.text(title.toUpperCase(), MARGIN, y)
	if (rightText) {
		document.setFont("helvetica", "normal")
		document.setFontSize(8)
		document.setTextColor(...GRAY)
		document.text(rightText, PAGE_WIDTH - MARGIN, y, { align: "right" })
	}
	document.setDrawColor(...GREEN)
	document.setLineWidth(0.45)
	document.line(MARGIN, y + 3, PAGE_WIDTH - MARGIN, y + 3)
	return y + 9
}

function planningRowHeight(document: jsPDF, row: ReportRow): number {
	const labelWidth = 55
	document.setFontSize(10.5)
	document.setFont("helvetica", "normal")
	const labelLines = lines(document, row.label, labelWidth)
	document.setFont("helvetica", "bold")
	const valueLines = lines(document, row.value, CONTENT_WIDTH - labelWidth - 13)
	return Math.max(9, Math.max(labelLines.length, valueLines.length) * 5 + 3)
}

function planningRow(document: jsPDF, row: ReportRow, y: number): number {
	const labelWidth = 55
	document.setFontSize(10.5)
	document.setFont("helvetica", "normal")
	const labelLines = lines(document, row.label, labelWidth)
	document.setFont("helvetica", "bold")
	const valueLines = lines(document, row.value, CONTENT_WIDTH - labelWidth - 13)
	const height = planningRowHeight(document, row)
	document.setFont("helvetica", "normal")
	document.setTextColor(...GRAY)
	document.text(labelLines, MARGIN + 5, y + 5)
	document.setFont("helvetica", "bold")
	document.setTextColor(...BLACK)
	document.text(valueLines, PAGE_WIDTH - MARGIN - 5, y + 5, { align: "right" })
	document.setDrawColor(...LINE)
	document.line(MARGIN + 5, y + height, PAGE_WIDTH - MARGIN - 5, y + height)
	return y + height
}

function constraintLines(document: jsPDF, content: ParcelReportV2Content): string[] {
	document.setFont("helvetica", "bold")
	document.setFontSize(11)
	return content.constraints.length
		? content.constraints.flatMap(value => lines(document, value, CONTENT_WIDTH - 12))
		: [content.noConstraintsLabel]
}

function checkedConstraintLayout(
	document: jsPDF,
	content: ParcelReportV2Content
): { labelWidth: number; lines: string[]; height: number } {
	document.setFont("helvetica", "bold")
	document.setFontSize(8)
	const labelWidth = Math.max(47, document.getTextWidth(content.checkedLabel.toUpperCase()) + 7)
	document.setFont("helvetica", "normal")
	document.setFontSize(9)
	const checkedLines = lines(document, content.checkedConstraints.join(" · "), CONTENT_WIDTH - labelWidth - 10)
	return {
		labelWidth,
		lines: checkedLines,
		height: Math.max(12, checkedLines.length * 4.5 + 5),
	}
}

function planningCardHeight(document: jsPDF, content: ParcelReportV2Content): number {
	let height = 9
	height += content.planningRows.length
		? content.planningRows.reduce((total, row) => total + planningRowHeight(document, row), 0)
		: 11
	height += 7 + 8 + 9
	height += constraintLines(document, content).length * 5 + 7
	if (content.checkedConstraints.length) height += checkedConstraintLayout(document, content).height + 5
	return height
}

function renderSummary(document: jsPDF, content: ParcelReportV2Content): number {
	document.setDrawColor(...LINE)
	document.setFillColor(255, 255, 255)
	document.roundedRect(MARGIN, 35, CONTENT_WIDTH, 28, 3, 3, "FD")
	document.setFont("helvetica", "bold")
	document.setFontSize(18)
	document.setTextColor(...BLACK)
	document.text(content.title, MARGIN + 7, 48)
	document.setFont("helvetica", "normal")
	document.setFontSize(11)
	document.setTextColor(...GRAY)
	document.text(lines(document, content.subtitle, CONTENT_WIDTH - 14), MARGIN + 7, 56)

	let y = heading(document, content.planningTitle, 74, content.planningSource)
	const cardTop = y
	document.setFillColor(...PALE)
	document.setDrawColor(...LINE)
	document.roundedRect(MARGIN, cardTop, CONTENT_WIDTH, planningCardHeight(document, content), 2, 2, "FD")
	y += 5
	document.setFont("helvetica", "bold")
	document.setFontSize(8)
	document.setTextColor(...GRAY)
	document.text(content.planningPoliciesLabel.toUpperCase(), MARGIN + 5, y)
	y += 4
	if (content.planningRows.length) {
		for (const row of content.planningRows) y = planningRow(document, row, y)
	} else {
		document.setFont("helvetica", "normal")
		document.setFontSize(9)
		document.text(content.noPlanningPoliciesLabel, MARGIN + 5, y + 5)
		y += 11
	}
	y += 7
	document.setDrawColor(198, 200, 202)
	document.line(MARGIN + 5, y, PAGE_WIDTH - MARGIN - 5, y)
	y += 8
	document.setFont("helvetica", "bold")
	document.setFontSize(8)
	document.setTextColor(...GRAY)
	document.text(content.constraintsLabel.toUpperCase(), MARGIN + 5, y)
	y += 9
	document.setFontSize(11)
	document.setTextColor(...GREEN)
	const renderedConstraintLines = constraintLines(document, content)
	document.text(renderedConstraintLines, MARGIN + 5, y)
	y += renderedConstraintLines.length * 5 + 7
	if (content.checkedConstraints.length) {
		const checked = checkedConstraintLayout(document, content)
		document.setFillColor(255, 255, 255)
		document.setDrawColor(...LINE)
		document.roundedRect(MARGIN + 5, y, CONTENT_WIDTH - 10, checked.height, 2, 2, "FD")
		document.setFont("helvetica", "bold")
		document.setFontSize(8)
		document.setTextColor(...GRAY)
		document.text(content.checkedLabel.toUpperCase(), MARGIN + 9, y + 6.5)
		document.setFont("helvetica", "normal")
		document.setFontSize(9)
		document.setTextColor(...BLACK)
		document.text(checked.lines, MARGIN + 5 + checked.labelWidth, y + 6.5)
		y += checked.height + 5
	}
	y += 7
	const gap = 3
	const metricWidth = content.metrics.length
		? (CONTENT_WIDTH - gap * (content.metrics.length - 1)) / content.metrics.length
		: 0
	for (const [index, metric] of content.metrics.entries()) {
		const x = MARGIN + index * (metricWidth + gap)
		document.roundedRect(x, y, metricWidth, 21, 2, 2, "S")
		document.setFont("helvetica", "bold")
		document.setFontSize(7.5)
		document.setTextColor(...GRAY)
		document.text(lines(document, metric.label.toUpperCase(), metricWidth - 8), x + 4, y + 7)
		document.setFontSize(16)
		document.setTextColor(...BLACK)
		document.text(lines(document, metric.value, metricWidth - 8), x + 4, y + 16)
	}
	return y + (content.metrics.length ? 21 : 0) + 9
}

function detailCellHeight(document: jsPDF, row: ReportRow | undefined, width: number): number {
	if (!row) return 0
	document.setFontSize(10)
	document.setFont("helvetica", "normal")
	const labelLines = lines(document, row.label, width * 0.45)
	document.setFont("helvetica", "bold")
	const valueLines = lines(document, row.value, width * 0.55 - 2)
	return Math.max(8, Math.max(labelLines.length, valueLines.length) * 5.1 + 2)
}

function detailCell(document: jsPDF, row: ReportRow | undefined, x: number, y: number, width: number): void {
	if (!row) return
	document.setFontSize(10)
	document.setFont("helvetica", "normal")
	const labelLines = lines(document, row.label, width * 0.45)
	document.setFont("helvetica", "bold")
	const valueLines = lines(document, row.value, width * 0.55 - 2)
	document.setFont("helvetica", "normal")
	document.setTextColor(...GRAY)
	document.text(labelLines, x, y + 4)
	document.setFont("helvetica", "bold")
	document.setTextColor(...BLACK)
	document.text(valueLines, x + width, y + 4, { align: "right" })
}

function renderSection(document: jsPDF, section: ReportSection, startY: number): number {
	let y = startY
	const gap = 10
	const columnWidth = (CONTENT_WIDTH - gap) / 2
	y = heading(document, section.title, y)
	for (let index = 0; index < section.rows.length; index += 2) {
		const left = section.rows[index]
		const right = section.rows[index + 1]
		const height = Math.max(
			detailCellHeight(document, left, columnWidth),
			detailCellHeight(document, right, columnWidth)
		)
		detailCell(document, left, MARGIN, y, columnWidth)
		detailCell(document, right, MARGIN + columnWidth + gap, y, columnWidth)
		y += height
		document.setDrawColor(...LINE)
		document.setLineWidth(0.15)
		document.line(MARGIN, y, MARGIN + columnWidth, y)
		if (right) document.line(MARGIN + columnWidth + gap, y, PAGE_WIDTH - MARGIN, y)
	}
	if (section.highlight) {
		y += 4
		document.setFillColor(...PALE)
		document.setDrawColor(205, 207, 209)
		document.roundedRect(MARGIN, y, CONTENT_WIDTH, 14, 2, 2, "FD")
		document.setFont("helvetica", "normal")
		document.setFontSize(9)
		document.setTextColor(...BLACK)
		document.text(section.highlight.label, MARGIN + 5, y + 9)
		document.setFont("helvetica", "bold")
		document.setFontSize(15)
		document.setTextColor(...GREEN)
		document.text(section.highlight.value, PAGE_WIDTH - MARGIN - 5, y + 10, { align: "right" })
		y += 14
	}
	return y + 9
}

function renderQrPanel(document: jsPDF, content: ParcelReportV2Content, y: number): number {
	const qr = encode(content.parcelUrl, { ecc: "M", border: 0 })
	const qrSize = 22
	const moduleSize = qrSize / (qr.size + 8)
	const textX = MARGIN + 35
	const textWidth = CONTENT_WIDTH - 41
	document.setFont("helvetica", "bold")
	document.setFontSize(10)
	const titleLines = lines(document, content.qrTitle, textWidth)
	document.setFont("helvetica", "normal")
	document.setFontSize(9)
	const descriptionLines = lines(document, content.qrDescription, textWidth)
	const panelHeight = Math.max(30, titleLines.length * 5 + descriptionLines.length * 4.5 + 12)

	document.setDrawColor(...LINE)
	document.roundedRect(MARGIN, y, CONTENT_WIDTH, panelHeight, 3, 3, "S")
	document.setFillColor(0, 0, 0)
	for (let row = 0; row < qr.size; row++) {
		for (let column = 0; column < qr.size; column++) {
			if (qr.data[row][column]) {
				document.rect(
					MARGIN + 5 + (column + 4) * moduleSize,
					y + (panelHeight - qrSize) / 2 + (row + 4) * moduleSize,
					moduleSize,
					moduleSize,
					"F"
				)
			}
		}
	}
	document.setFont("helvetica", "bold")
	document.setFontSize(10)
	document.setTextColor(...BLACK)
	document.text(titleLines, textX, y + 11)
	document.setFont("helvetica", "normal")
	document.setFontSize(9)
	document.setTextColor(...GRAY)
	document.text(descriptionLines, textX, y + 11 + titleLines.length * 5 + 1)
	document.link(MARGIN, y, CONTENT_WIDTH, panelHeight, { url: content.parcelUrl })
	return y + panelHeight + 5
}

function renderMap(document: jsPDF, map: ReportMap, attribution: string, y: number): number {
	y = heading(document, map.title, y)
	const image = document.getImageProperties(map.image)
	const mapHeight = (CONTENT_WIDTH * image.height) / image.width
	document.addImage(map.image, "PNG", MARGIN, y, CONTENT_WIDTH, mapHeight)
	document.setDrawColor(...LINE)
	document.rect(MARGIN, y, CONTENT_WIDTH, mapHeight, "S")
	document.setFont("helvetica", "normal")
	document.setFontSize(7)
	document.setTextColor(...GRAY)
	document.text(attribution, PAGE_WIDTH - MARGIN, y + mapHeight + 4, { align: "right" })
	return y + mapHeight + 12
}

function renderContent(document: jsPDF, content: ParcelReportV2Content, logoDataUrl: string): number {
	header(document, content, logoDataUrl)
	let y = renderSummary(document, content)
	for (const section of content.sections) {
		if (section.rows.length || section.highlight) y = renderSection(document, section, y)
	}
	y = renderQrPanel(document, content, y)
	document.setFont("helvetica", "normal")
	document.setFontSize(8)
	document.setTextColor(...GRAY)
	const disclaimerLines = lines(document, content.disclaimer, CONTENT_WIDTH)
	document.text(disclaimerLines, MARGIN, y + 2)
	y += disclaimerLines.length * 4 + 2
	for (const map of content.maps) y = renderMap(document, map, content.mapAttribution, y + 12)
	return y
}

function renderFooter(document: jsPDF, content: ParcelReportV2Content, pageHeight: number): void {
	document.setDrawColor(...LINE)
	document.line(0, pageHeight - 14, PAGE_WIDTH, pageHeight - 14)
	document.setFont("helvetica", "normal")
	document.setFontSize(7.5)
	document.setTextColor(...GRAY)
	document.text(content.footerBrand, MARGIN, pageHeight - 8)
	document.text(content.footerSource, PAGE_WIDTH - MARGIN, pageHeight - 8, { align: "right" })
}

export function create(content: ParcelReportV2Content, logoDataUrl: string): jsPDF {
	const measuringDocument = new jsPDF({ format: "a4", unit: "mm" })
	const pageHeight = renderContent(measuringDocument, content, logoDataUrl) + FOOTER_SPACE
	const document = new jsPDF({ format: [PAGE_WIDTH, pageHeight], unit: "mm" })
	document.setProperties({ title: content.title })
	renderContent(document, content, logoDataUrl)
	renderFooter(document, content, pageHeight)
	return document
}
